/**
 * Review queue for HELD invoice->ticket links (#3464).
 *
 * Reads are client-direct (rules: Finance module + org match). Every verdict goes through the
 * P4 callables - never a client write. A verdict is a human decision that moves revenue into or
 * out of live P&L, so the server re-checks status, org and realm inside one transaction.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  query,
  where,
  getDocs,
  getCountFromServer,
  documentId,
} from 'firebase/firestore';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import toast from 'react-hot-toast';
import { db, functions } from '../../config/firebase';
import { useOrganization } from '../../contexts/OrganizationContext';
import { COLLECTIONS } from '../../config/collections';
import { idInChunks } from '../../lib/firestore/idInChunks';
import type { InvoiceServiceLink } from '../../types/collections';
import { invoiceServiceLinkKeys } from './useInvoiceServiceLinks';
import { linkedWorkKeys } from './linkedWorkKeys';

export type ReviewVerdict = 'promote' | 'dismiss';
export type ReviewOutcome =
  | 'promoted'
  | 'dismissed'
  | 'already_reviewed'
  | 'note_required'
  | 'sibling_active'
  | 'ticket_missing'
  | 'unverifiable'
  | 'single_review_required'
  | 'not_found'
  | 'org_mismatch'
  | 'error';

export interface ReviewResult {
  linkId: string;
  outcome: ReviewOutcome;
  error?: string;
}

const IN_CHUNK = 30; // Firestore `in` limit
// The ticket read by document id is chunked by idInChunks (lib/firestore/idInChunks.ts): the live rules deny that shape
// for a non-admin past ~19 ids (measured; #6910 shipped with every Confirm disabled). The Service ID read below is a
// different query shape and keeps the full 30.
const MAX_BULK = 50; // server cap per call

const chunk = <T,>(items: readonly T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

async function fetchNeedsReview(organizationId: string): Promise<InvoiceServiceLink[]> {
  const q = query(
    collection(db, COLLECTIONS.INVOICE_SERVICE_LINKS),
    where('organizationId', '==', organizationId),
    where('status', '==', 'needs_review'),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as InvoiceServiceLink)
    // Best evidence first; ties by invoice number so the order is stable between refetches.
    .sort((a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0) || String(a.docNumber).localeCompare(String(b.docNumber)));
}

/** Held rows for the queue. The list length and the badge count are both server truths (no cap). */
export function useNeedsReviewInvoiceServiceLinks() {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.organizationId ?? '';
  return useQuery({
    queryKey: invoiceServiceLinkKeys.needsReview(organizationId),
    queryFn: () => fetchNeedsReview(organizationId),
    enabled: !!organizationId,
  });
}

/** Nav badge: getCountFromServer, never limit-then-count (a capped page that called itself the count, #6883). */
export function useNeedsReviewInvoiceServiceLinkCount() {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.organizationId ?? '';
  return useQuery({
    queryKey: invoiceServiceLinkKeys.needsReviewCount(organizationId),
    queryFn: async () => {
      const q = query(
        collection(db, COLLECTIONS.INVOICE_SERVICE_LINKS),
        where('organizationId', '==', organizationId),
        where('status', '==', 'needs_review'),
      );
      return (await getCountFromServer(q)).data().count;
    },
    enabled: !!organizationId,
    staleTime: 60_000,
  });
}

export interface ReviewContext {
  /** serviceTickets doc id -> realmId ('' when unstamped). */
  ticketRealms: Map<string, string>;
  /** Service ID -> invoiceDocIds of ACTIVE links on that ticket. */
  activeByService: Map<string, string[]>;
}

async function fetchReviewContext(
  organizationId: string,
  ticketIds: readonly string[],
  serviceIds: readonly string[],
): Promise<ReviewContext> {
  const ticketRealms = new Map<string, string>();
  const activeByService = new Map<string, string[]>();
  await Promise.all([
    ...idInChunks(COLLECTIONS.SERVICE_TICKETS, ticketIds).map(async (ids) => {
      const snap = await getDocs(
        query(
          collection(db, COLLECTIONS.SERVICE_TICKETS),
          where('organizationId', '==', organizationId),
          where(documentId(), 'in', ids),
        ),
      );
      for (const d of snap.docs) {
        const realm = d.data().realmId;
        ticketRealms.set(d.id, typeof realm === 'string' ? realm.trim() : '');
      }
    }),
    ...chunk(serviceIds, IN_CHUNK).map(async (ids) => {
      const snap = await getDocs(
        query(
          collection(db, COLLECTIONS.INVOICE_SERVICE_LINKS),
          where('organizationId', '==', organizationId),
          where('serviceId', 'in', ids),
          where('status', '==', 'active'),
        ),
      );
      for (const d of snap.docs) {
        const l = d.data() as Partial<InvoiceServiceLink>;
        if (!l.serviceId || !l.invoiceDocId) continue;
        activeByService.set(l.serviceId, [...(activeByService.get(l.serviceId) ?? []), l.invoiceDocId]);
      }
    }),
  ]);
  return { ticketRealms, activeByService };
}

/** Ticket realms + already-active siblings for the held rows. Failure is surfaced, never treated as "no warnings". */
export function useLinkReviewContext(links: readonly InvoiceServiceLink[] | undefined) {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.organizationId ?? '';
  const ticketIds = Array.from(new Set((links ?? []).map((l) => l.serviceTicketId).filter(Boolean))).sort();
  const serviceIds = Array.from(new Set((links ?? []).map((l) => l.serviceId ?? l.appSheetServiceId).filter(Boolean))).sort();
  return useQuery({
    // The key carries the FULL id sets: a count would serve a stale context when one held row leaves and another arrives.
    queryKey: [...invoiceServiceLinkKeys.reviewContext(organizationId, serviceIds), 'tickets', ticketIds.join(',')],
    queryFn: () => fetchReviewContext(organizationId, ticketIds, serviceIds),
    enabled: !!organizationId && !!links && links.length > 0,
    // Sibling status changes with no key change (the nightly job activates a link), so never trust a cached copy.
    staleTime: 0,
  });
}

function invalidateAll(queryClient: ReturnType<typeof useQueryClient>, organizationId: string) {
  queryClient.invalidateQueries({ queryKey: invoiceServiceLinkKeys.needsReview(organizationId) });
  queryClient.invalidateQueries({ queryKey: invoiceServiceLinkKeys.needsReviewCount(organizationId) });
  queryClient.invalidateQueries({ queryKey: invoiceServiceLinkKeys.byOrg(organizationId) });
  queryClient.invalidateQueries({ queryKey: [...invoiceServiceLinkKeys.all, 'reviewContext', organizationId] });
  // Any invoice's own link list may have gained/lost this row.
  queryClient.invalidateQueries({ queryKey: [...invoiceServiceLinkKeys.all, 'forInvoice'] });
  // ...and the Collect rows' / To Send cards' per-invoice linked-work chips (L-1263).
  queryClient.invalidateQueries({ queryKey: linkedWorkKeys.forInvoiceAll() });
}

const OUTCOME_TOAST: Record<ReviewOutcome, string> = {
  promoted: 'Link confirmed',
  dismissed: 'Link dismissed',
  already_reviewed: 'Someone already decided this link - the list has been refreshed',
  note_required: 'A note is required to confirm a link across QuickBooks files',
  sibling_active: 'That ticket is already billed on another invoice - remove that link first',
  ticket_missing: 'That ticket no longer exists, so the link cannot be confirmed (dismiss it instead)',
  unverifiable: 'This link has no Service ID, so the double-billing check cannot run - it was not confirmed',
  single_review_required: 'Needs individual review (shared ticket or no realm stamp) - confirm it on its own',
  not_found: 'That link no longer exists',
  org_mismatch: 'That link belongs to another organization',
  error: 'Could not save that decision',
};

/** Optimistically drop the decided rows from the cached list; return the snapshot for rollback. */
function useOptimisticRemoval(organizationId: string) {
  const queryClient = useQueryClient();
  const key = invoiceServiceLinkKeys.needsReview(organizationId);
  const countKey = invoiceServiceLinkKeys.needsReviewCount(organizationId);
  return {
    queryClient,
    async remove(ids: readonly string[]) {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<InvoiceServiceLink[]>(key);
      const previousCount = queryClient.getQueryData<number>(countKey);
      const drop = new Set(ids);
      if (previous) queryClient.setQueryData(key, previous.filter((l) => !drop.has(l.id)));
      return { previous, previousCount };
    },
    rollback(snapshot?: { previous?: InvoiceServiceLink[]; previousCount?: number }) {
      if (snapshot?.previous !== undefined) queryClient.setQueryData(key, snapshot.previous);
      if (snapshot?.previousCount !== undefined) queryClient.setQueryData(countKey, snapshot.previousCount);
    },
  };
}

export interface ReviewInput {
  linkId: string;
  verdict: ReviewVerdict;
  note?: string;
}

export function useReviewInvoiceServiceLink() {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.organizationId ?? '';
  const opt = useOptimisticRemoval(organizationId);

  return useMutation({
    mutationFn: async (input: ReviewInput): Promise<ReviewResult> => {
      const callable = httpsCallable<Record<string, unknown>, ReviewResult>(functions, 'fieldService_reviewInvoiceServiceLink');
      const res = await callable({
        organizationId,
        linkId: input.linkId,
        verdict: input.verdict,
        expectedStatus: 'needs_review',
        ...(input.note ? { note: input.note } : {}),
      });
      return res.data;
    },
    onMutate: (input) => opt.remove([input.linkId]),
    onSuccess: (result) => {
      // Only a written verdict is a success. note_required / not_found / org_mismatch wrote nothing.
      const wrote = result.outcome === 'promoted' || result.outcome === 'dismissed';
      (wrote ? toast.success : toast.error)(OUTCOME_TOAST[result.outcome] ?? OUTCOME_TOAST.error);
    },
    onError: (error: Error, _input, snapshot) => {
      opt.rollback(snapshot);
      toast.error(OUTCOME_TOAST.error);
    },
    onSettled: () => invalidateAll(opt.queryClient, organizationId),
  });
}

export interface BulkReviewInput {
  linkIds: string[];
  verdict: ReviewVerdict;
  note?: string;
}

/** Bulk verdict: per-id results, partial success is normal. Splits into server-sized calls. */
export function useBulkReviewInvoiceServiceLinks() {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.organizationId ?? '';
  const opt = useOptimisticRemoval(organizationId);

  return useMutation({
    mutationFn: async (input: BulkReviewInput): Promise<ReviewResult[]> => {
      const callable = httpsCallable<Record<string, unknown>, { results: ReviewResult[] }>(
        functions,
        'fieldService_bulkReviewInvoiceServiceLinks',
      );
      const results: ReviewResult[] = [];
      for (const ids of chunk(input.linkIds, MAX_BULK)) {
        try {
          const res = await callable({
            organizationId,
            linkIds: ids,
            verdict: input.verdict,
            expectedStatus: 'needs_review',
            ...(input.note ? { note: input.note } : {}),
          });
          results.push(...res.data.results);
        } catch (err) {
          // A failed call must not hide the chunks that already landed.
          for (const linkId of ids) {
            results.push({ linkId, outcome: 'error', error: err instanceof Error ? err.message : 'unknown error' });
          }
        }
      }
      return results;
    },
    onMutate: (input) => opt.remove(input.linkIds),
    onSuccess: (results, _input, snapshot) => {
      const wrote = (r: ReviewResult) => r.outcome === 'promoted' || r.outcome === 'dismissed';
      const ok = results.filter(wrote).length;
      const failed = results.length - ok;
      // Rows that did NOT get a verdict go back on the list now (the refetch then settles truth). Built from the
      // CURRENT cache, so a row another reviewer decided in the meantime is not resurrected.
      const notWritten = new Set(results.filter((r) => !wrote(r)).map((r) => r.linkId));
      if (notWritten.size > 0 && snapshot?.previous) {
        const key = invoiceServiceLinkKeys.needsReview(organizationId);
        const current = opt.queryClient.getQueryData<InvoiceServiceLink[]>(key) ?? [];
        const have = new Set(current.map((l) => l.id));
        const restore = snapshot.previous.filter((l) => notWritten.has(l.id) && !have.has(l.id));
        const order = new Map(snapshot.previous.map((l, i) => [l.id, i] as const));
        opt.queryClient.setQueryData(key, [...current, ...restore].sort((x, y) => (order.get(x.id) ?? 0) - (order.get(y.id) ?? 0)));
      }
      if (failed === 0) toast.success(`${ok} link${ok === 1 ? '' : 's'} saved`);
      else toast.error(`${ok} saved, ${failed} not saved - they are still in the list`);
    },
    onError: (error: Error, _input, snapshot) => {
      opt.rollback(snapshot);
      toast.error(OUTCOME_TOAST.error);
    },
    onSettled: () => invalidateAll(opt.queryClient, organizationId),
  });
}

/**
 * Held (`needs_review`) links on ONE invoice, so InvoiceDetail can say "held for review" instead of
 * silently hiding the row (the active-only reader drops it, which reads as "no ticket").
 */
export function useHeldInvoiceServiceLinksForInvoice(invoiceDocId?: string) {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.organizationId ?? '';
  return useQuery({
    queryKey: [...invoiceServiceLinkKeys.forInvoice(invoiceDocId ?? ''), 'held', organizationId],
    queryFn: async (): Promise<InvoiceServiceLink[]> => {
      const snap = await getDocs(
        query(
          collection(db, COLLECTIONS.INVOICE_SERVICE_LINKS),
          where('organizationId', '==', organizationId),
          where('invoiceDocId', '==', invoiceDocId ?? ''),
        ),
      );
      return snap.docs
        .map((d) => ({ id: d.id, ...d.data() }) as InvoiceServiceLink)
        .filter((l) => l.status === 'needs_review');
    },
    enabled: !!organizationId && !!invoiceDocId,
  });
}
